/**
 * Floating "Ask ClubBot" launcher.
 *
 * Replaces the old full-width bottom bar with a small bubble pinned bottom-right:
 *   - Desktop (≥sm): toggles a docked popup chat window that embeds the real
 *     <Chat>, so it's a true conversation surface (history, streaming, chips),
 *     not a one-shot quick-ask. ⌘K toggles it; Esc closes it.
 *   - Mobile (<sm): a docked window would fight the soft keyboard and cramp the
 *     screen, so the bubble just routes to the full-screen "Ask" tab.
 *
 * Mounted by AppShell on every tab except "chat" (where the tab IS the chat).
 */
import { useEffect, useState } from "react";
import { Sparkles, X } from "lucide-react";

import { Chat } from "../chat/Chat";
import { useIsDesktop } from "../lib/useIsDesktop";
import type { Me } from "../api";

export function ChatDock({
  me,
  onSignOut,
  onMobileAsk,
}: {
  me: Me;
  onSignOut: () => void;
  /** Mobile-only: bubble tap routes here (the parent navigates to the Ask tab). */
  onMobileAsk: () => void;
}) {
  const isDesktop = useIsDesktop();
  const [open, setOpen] = useState(false);
  // Lazily mount <Chat> on first open, then keep it mounted (just hidden) so the
  // conversation + scroll position survive toggling — and we don't refetch
  // history on every open. Avoids streaming-into-an-unmounted-component too.
  const [mountedOnce, setMountedOnce] = useState(false);

  // ⌘K / Ctrl+K toggles the dock (desktop only — there's no keyboard on mobile).
  useEffect(() => {
    if (!isDesktop) return;
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setMountedOnce(true);
        setOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isDesktop]);

  // Esc closes the open popup.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  function handleBubble() {
    if (isDesktop) {
      setMountedOnce(true);
      setOpen((v) => !v);
    } else {
      onMobileAsk();
    }
  }

  return (
    <>
      {/* Desktop popup chat window. Lazy-mounted, then kept in the DOM (hidden)
          across toggles so the conversation isn't reset each time. */}
      {isDesktop && mountedOnce && (
        <div
          className={
            "fixed bottom-6 right-6 z-40 flex flex-col " +
            "w-[400px] max-w-[calc(100vw-3rem)] h-[600px] max-h-[calc(100dvh-7rem)] " +
            "bg-bg border-2 border-ink rounded-xl shadow-2xl overflow-hidden cs-modal-card " +
            (open ? "" : "hidden")
          }
          role="dialog"
          aria-label="Ask ClubBot"
        >
          <div className="px-4 py-3 bg-ink text-bg flex items-center justify-between shrink-0">
            <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.16em]">
              <Sparkles className="h-3.5 w-3.5 text-accent" /> Ask ClubBot
            </div>
            <button
              onClick={() => setOpen(false)}
              aria-label="Close"
              className="text-bg/60 hover:text-bg cs-tap"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="flex-1 min-h-0">
            <Chat me={me} onSignOut={onSignOut} embedded />
          </div>
        </div>
      )}

      {/* Floating "Ask ClubBot" launcher — sparkle + label so it reads as the AI
          assistant, not a generic chat bubble. Hidden on desktop while open. */}
      {!(isDesktop && open) && (
        <button
          onClick={handleBubble}
          aria-label="Ask ClubBot"
          className="group fixed bottom-6 right-6 z-40 flex items-center gap-2 rounded-full bg-ink text-bg border-2 border-accent pl-4 pr-5 py-3 shadow-lg shadow-accent/20 cs-tap hover:brightness-110 hover:shadow-accent/40 transition"
          style={{ marginBottom: "env(safe-area-inset-bottom)" }}
        >
          <Sparkles className="h-5 w-5 text-accent transition-transform group-hover:rotate-12" />
          <span className="font-display font-bold text-sm leading-none">Ask ClubBot</span>
        </button>
      )}
    </>
  );
}
